/**
 * Interior fractional positions for `count` dots over `length` days.
 * Two dots land near the 1/3 and 2/3 marks, not on the boundary days.
 */
export const computeInteriorSpacedIndices = (
  length: number,
  count: number,
): number[] => {
  if (count <= 0 || length <= 0) return [];
  if (count >= length) return Array.from({ length }, (_, i) => i);

  if (count === 1) {
    return [Math.floor((length - 1) / 2)];
  }

  const indices: number[] = [];
  const used = new Set<number>();

  for (let i = 0; i < count; i += 1) {
    let idx = Math.floor(((i + 1) * length) / (count + 1));
    idx = Math.min(length - 1, Math.max(0, idx));

    while (used.has(idx) && idx < length - 1) idx += 1;
    while (used.has(idx) && idx > 0) idx -= 1;

    used.add(idx);
    indices.push(idx);
  }

  return indices;
};

/**
 * Select `count` day indices from a timeline, spread at interior fractions.
 * Required indices (specific days) snap to the nearest ideal slot so all dots
 * are chosen from one spacing pass over the full range.
 */
export const selectSpacedDayIndices = (
  length: number,
  count: number,
  requiredIndices: number[] = [],
): number[] => {
  if (count <= 0 || length <= 0) return [];

  const sortedRequired = [...new Set(requiredIndices)].sort((a, b) => a - b);

  const slots = computeInteriorSpacedIndices(length, count);
  const fixedSlotPositions = new Set<number>();

  sortedRequired.forEach((requiredIdx) => {
    let bestSlot = -1;
    let bestDistance = Infinity;

    for (let slotPos = 0; slotPos < slots.length; slotPos += 1) {
      if (!fixedSlotPositions.has(slotPos)) {
        const distance = Math.abs(slots[slotPos] - requiredIdx);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestSlot = slotPos;
        }
      }
    }

    if (bestSlot === -1) return;

    slots[bestSlot] = requiredIdx;
    fixedSlotPositions.add(bestSlot);
  });

  return [...slots].sort((a, b) => a - b);
};

/** Map interior-spaced indices back to Date objects. */
export const pickEvenlySpacedDays = (days: Date[], count: number): Date[] => {
  if (count <= 0 || days.length === 0) return [];
  if (count >= days.length) return [...days];

  return selectSpacedDayIndices(days.length, count).map((idx) => days[idx]);
};

/**
 * Choose gray-dot days within an admin-period window.
 *
 * All dots — including those on explicit specific days — are chosen from a
 * single interior spacing pass over the full available range.
 */
export const grayDotDaysInRange = (
  available: Date[],
  specificDayIndices: number[],
  count: number,
  dayOfWeek: (date: Date) => number = (date) => date.getDay(),
): Date[] => {
  if (count <= 0 || available.length === 0) return [];
  if (count >= available.length) return [...available];

  const requiredIndices = available.reduce<number[]>((indices, day, index) => {
    if (specificDayIndices.includes(dayOfWeek(day))) {
      indices.push(index);
    }
    return indices;
  }, []);

  if (specificDayIndices.length > 0 && requiredIndices.length > count) {
    return requiredIndices.slice(0, count).map((idx) => available[idx]);
  }

  return selectSpacedDayIndices(available.length, count, requiredIndices).map(
    (idx) => available[idx],
  );
};
